/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, Subscription, throwError } from 'rxjs';
import { filter, mergeMap, skip, take } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { PatientSharedService } from '../../../services/shared/patient-shared/patient-shared.service';
import { Store } from '@ngrx/store';
import { UserActionTypes } from '../../../store/user/user.action';
import { TranslateService } from '@ngx-translate/core';

export type PatientTabs = 'studies' | 'instances' | 'diaries';

/**
 * Component:
 * Patient details page displaying the selected patient;
 * Can be found: {uri}/patients/{{user_id}}
 */
@Component({
  selector: 'app-patient-detail',
  templateUrl: './patient-detail.component.html',
  styleUrls: ['./patient-detail.component.scss'],
})
export class PatientDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('matTabGroup') matTabGroup: QueryList<MatTabGroup>;

  // Icons
  faCheckCircle = faCheckCircle;
  faTimesCircle = faTimesCircle;

  public isLoading$: Observable<boolean>;

  // Data provided by UserService
  public user: UserInterface;

  public activeChildrouteSubject = new BehaviorSubject<PatientTabs | ''>('');

  private translationSubject = new BehaviorSubject<any>({});

  private isLoadingSubject = new BehaviorSubject<boolean>(true);

  private patientId: number;

  private myMembers$: Observable<UserInterface[]>;
  private myRegisteredUsers$: Observable<UserInterface[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private translateService: TranslateService,
    private actRoute: ActivatedRoute,
    private router: Router,
    private store: Store<{
      myMembers: UserInterface[];
      myRegisteredUsers: UserInterface[],
    }>,
    private sharedService: PatientSharedService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.myMembers$ = this.store.select('myMembers');
    this.myRegisteredUsers$ = this.store.select('myRegisteredUsers');

    this.subscriptions.push(
      this.translateService
        .getTranslation(localStorage.getItem('language') ?? 'en')
        .subscribe((translations) => {
          this.translationSubject.next(translations);
        })
    );
    this.subscriptions.push(
      this.translateService.onLangChange.subscribe((event) => {
        this.translationSubject.next(event.translations);
      }),
    );
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.isLoadingSubject.next(true);

    this.subscriptions.push(
      this.actRoute.params
        .pipe(
          mergeMap((params) => {
            this.patientId = params.id;
            this.sharedService.pushId(this.patientId);
            this.store.dispatch({
              type: UserActionTypes.getUsersRegisteredByEcoachType,
              payload: {},
            });
            const myRegisteredUsers = this.myRegisteredUsers$.pipe(skip(1), take(1));
            this.store.dispatch({
              type: UserActionTypes.getMyMembersType,
              payload: {},
            });
            const myMembers = this.myMembers$.pipe(skip(1), take(1));
            return forkJoin({ myRegisteredUsers, myMembers });
          }),
        )
        .subscribe({
          next: (results) => {
            const myPatients = this.helper.setUniqueMembers(results.myRegisteredUsers, results.myMembers);
            const patient = this.helper.findArrObjById(this.patientId, myPatients);
            if (patient) {
              this.sharedService.pushPatient(patient);
              this.user = patient;
              this.isLoadingSubject.next(false);
            } else {
              this.router.navigateByUrl('/patients');
            }
          },
          error: (error) => {
            throwError(() => error);
            this.router.navigateByUrl('/patients');
          },
        }),
    );

    setTimeout(() => {
      // Nav active
      this.subscriptions.push(
        this.sharedService.activeChildroute$.pipe(filter((value) => value !== null)).subscribe((value) => {
          this.activeChildrouteSubject.next(value);
          if (this.matTabGroup) {
            this.helper.setMatTabActive(
              this.matTabGroup,
              this.router.url,
              this.translationSubject,
              this.translateService,
              'patient-detail',
            );
          }
        }),
      );
    });
  }

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.matTabGroup.changes.pipe().subscribe({
        next: (_comps: QueryList<MatTabGroup>) => {
          this.helper.setMatTabActive(
            this.matTabGroup,
            this.router.url,
            this.translationSubject,
            this.translateService,
            'patient-detail',
          )},
      }),
    );
  }

  // Listens to tab change
  public onChangeTab(event: MatTabChangeEvent): void {
    const evaluatedTab = this.evaluateTab(event.tab.textLabel, this.translationSubject.value);
    if (evaluatedTab !== '' && this.activeChildrouteSubject.value !== evaluatedTab) {
      this.router.navigate([evaluatedTab], { relativeTo: this.actRoute });
    }
  }

  private evaluateTab(label: string, translate: any): PatientTabs | '' {
    if (translate) {
      if (translate['patient-detail']['studies'] === label) {
        return 'studies';
      }
      if (translate['patient-detail']['instances'] === label) {
        return 'instances';
      }
      if (translate['patient-detail']['diaries'] === label) {
        return 'diaries';
      }
    }
    return '';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
